import React, { useState, useEffect, useRef } from "react";
import { ChevronDown, Menu, Phone } from "lucide-react";
import ButtonDark from "./ui/button/buttonDark";
import ButtonLight from "./ui/button/buttonLight";
import { useScrollState } from "../hooks/useScrollState";

export default function Navbar() {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);
  const { scrollY, scrollDirection, isScrolling, isAtTop } = useScrollState();

  const toggleDropdown = (id: string) => {
    setOpenDropdown(openDropdown === id ? null : id);
  };

  const closeDropdown = () => {
    setOpenDropdown(null);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        closeDropdown();
      }
    };

    if (openDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdown]);

  // Close mobile menu on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  // Navbar visibility based on scroll state
  const shouldShowNavbar = isAtTop || scrollDirection === 'up' || scrollY < 100;
  const navbarClasses = `fixed top-0 left-0 right-0 z-[9999] shadow-[0_2px_5px_rgba(0,0,0,0.37)] transition-transform duration-300 ${
    shouldShowNavbar ? 'translate-y-0' : '-translate-y-full'
  }`;

  return (
    <>
    <div ref={navRef} className={navbarClasses}>
      {/* Banner - Hidden by default */}
      <div className="hidden border-b border-[rgb(230,230,230)] bg-white px-[5%] py-3.5 items-center justify-center">
        <div className="text-[rgb(102,102,102)] text-sm font-medium leading-6">
          This banner can be used to inform visitors of something important.
        </div>
        <a
          href="https://www.gesundheit-macht-sinn.de/ceragem#"
          className="ml-2.5 text-sm font-semibold text-[rgb(96,112,200)] flex items-center transition-opacity hover:opacity-80"
        >
          <span>Discover</span>
          <svg
            className="w-[18px] h-[18px] ml-1"
            fill="none"
            viewBox="0 0 18 18"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10.8698 4.28285C10.5773 4.57535 10.5773 5.04785 10.8698 5.34035L13.7798 8.25035H2.40234C1.98984 8.25035 1.65234 8.58785 1.65234 9.00035C1.65234 9.41285 1.98984 9.75035 2.40234 9.75035H13.7873L10.8773 12.6603C10.5848 12.9528 10.5848 13.4254 10.8773 13.7179C11.1698 14.0104 11.6423 14.0104 11.9348 13.7179L16.1273 9.52535C16.4198 9.23285 16.4198 8.76035 16.1273 8.46785L11.9273 4.28285C11.6423 3.99035 11.1623 3.99035 10.8698 4.28285Z"
              fill="currentColor"
            />
          </svg>
        </a>
      </div>

      {/* Main Navbar */}
      <div className="relative bg-white border-b border-[rgb(230,230,230)] px-[5%] py-3 z-[99] flex flex-col items-center justify-center">
        <div className="w-full max-w-[80rem] mx-auto flex items-center justify-between gap-4">
          {/* Logo */}
          <a
            href="https://www.gesundheit-macht-sinn.de/"
            aria-label="home"
            className="flex-shrink-0 relative no-underline"
          >
            <img
              src="https://cdn.prod.website-files.com/68aeae2ffd6189c35fbc6601/68aeb3463fae50c83f408bfa_GesundheitsmachtSinn-Rectangle-Full@4x.png"
              alt="Gesundheit macht Sinn"
              className="h-10 xl:h-12 max-w-full inline-block align-middle"
            />
          </a>

          {/* Navigation Menu */}
          <nav className="items-center hidden lg:flex justify-center gap-1 xl:gap-2 flex-1" role="navigation">
            <a
              href="#start"
              className="px-2 xl:px-4 py-2 font-monda text-sm xl:text-base font-medium text-[rgb(51,51,51)] no-underline transition-all hover:text-slate-900"
            >
              Start
            </a>
            <a
              href="#vorteile"
              className="px-2 xl:px-4 py-2 font-monda text-sm xl:text-base font-medium text-[rgb(51,51,51)] no-underline transition-all hover:text-slate-900"
            >
              Vorteile
            </a>
            <a
              href="#ceragem"
              className="px-2 xl:px-4 py-2 font-monda text-sm xl:text-base font-medium text-[rgb(51,51,51)] no-underline transition-all hover:text-slate-900"
            >
              Ceragem
            </a>
            <a
              href="#video"
              className="px-2 xl:px-4 py-2 font-monda text-sm xl:text-base font-medium text-[rgb(51,51,51)] no-underline transition-all hover:text-slate-900"
            >
              Video
            </a>
            <a
              href="#preise"
              className="px-2 xl:px-4 py-2 font-monda text-sm xl:text-base font-medium text-[rgb(51,51,51)] no-underline transition-all hover:text-slate-900"
            >
              Preise
            </a>
            <a
              href="#faq"
              className="px-2 xl:px-4 py-2 font-monda text-sm xl:text-base font-medium text-[rgb(51,51,51)] no-underline transition-all hover:text-slate-900"
            >
              FAQ
            </a>
          </nav>

          {/* Right: CTA Buttons */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="hidden lg:flex items-center gap-2 xl:gap-3">
              <ButtonDark className="whitespace-nowrap !text-xs xl:!text-sm !px-3 !py-1.5 xl:!px-4 xl:!py-2" onClick={() => window.location.href = 'tel:+49369584196'}>
                <span className="hidden xl:inline">Anrufen:</span><span className="xl:hidden">Tel:</span> 03695 / 84196
              </ButtonDark>
              <ButtonLight className="whitespace-nowrap !text-xs xl:!text-sm !px-3 !py-1.5 xl:!px-4 xl:!py-2" onClick={() => window.location.href = '#termin'}>
                Termin
              </ButtonLight>
            </div>
            {/* Mobile Menu Toggle */}
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-slate-600 transition-colors hover:text-slate-900"
              aria-label="Toggle mobile menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>
    </div>

    {/* Mobile Menu - Outside of transformed navbar container */}
    <div
      className={`lg:hidden fixed inset-0 top-[73px] bg-white z-[9998] overflow-y-auto transition-transform duration-300 ${
        isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
      }`}
    >
      <nav className="flex flex-col px-[5%] py-6 space-y-2">
        {/* Mobile Navigation Links */}
        <a
          href="#start"
          onClick={() => setIsMobileMenuOpen(false)}
          className="px-5 py-3 font-monda text-base font-medium text-[rgb(51,51,51)] no-underline transition-all hover:bg-gray-50 rounded-lg"
        >
          Start
        </a>
        <a
          href="#vorteile"
          onClick={() => setIsMobileMenuOpen(false)}
          className="px-5 py-3 font-monda text-base font-medium text-[rgb(51,51,51)] no-underline transition-all hover:bg-gray-50 rounded-lg"
        >
          Vorteile
        </a>
        <a
          href="#ceragem"
          onClick={() => setIsMobileMenuOpen(false)}
          className="px-5 py-3 font-monda text-base font-medium text-[rgb(51,51,51)] no-underline transition-all hover:bg-gray-50 rounded-lg"
        >
          Ceragem
        </a>
        <a
          href="#video"
          onClick={() => setIsMobileMenuOpen(false)}
          className="px-5 py-3 font-monda text-base font-medium text-[rgb(51,51,51)] no-underline transition-all hover:bg-gray-50 rounded-lg"
        >
          Video
        </a>
        <a
          href="#preise"
          onClick={() => setIsMobileMenuOpen(false)}
          className="px-5 py-3 font-monda text-base font-medium text-[rgb(51,51,51)] no-underline transition-all hover:bg-gray-50 rounded-lg"
        >
          Preise
        </a>
        <a
          href="#faq"
          onClick={() => setIsMobileMenuOpen(false)}
          className="px-5 py-3 font-monda text-base font-medium text-[rgb(51,51,51)] no-underline transition-all hover:bg-gray-50 rounded-lg"
        >
          FAQ
        </a>

        {/* Mobile CTA Buttons */}
        <div className="pt-4 space-y-3">
          <ButtonDark 
            className="w-full whitespace-nowrap" 
            onClick={() => {
              setIsMobileMenuOpen(false);
              window.location.href = 'tel:+49369584196';
            }}
          >
            Jetzt anrufen: 03695 / 84196
          </ButtonDark>
          <ButtonLight 
            className="w-full whitespace-nowrap" 
            onClick={() => {
              setIsMobileMenuOpen(false);
              window.location.href = '#termin';
            }}
          >
            Termin buchen
          </ButtonLight>
        </div>
      </nav>
    </div>
    </>
  );
}
